import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, ProtectedRoute, useAuth } from './components/AuthProvider';
import MobileLayout, { MobileBottomNavItem } from './components/MobileLayout';
import Icon from './components/icons/IconLibrary';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Home from './pages/Home';
import Transactions from './pages/Transactions';
import Accounts from './pages/Accounts';
import Budgets from './pages/Budgets';
import Invoices from './pages/Invoices';
import Credits from './pages/Credits';
import Fiscal from './pages/Fiscal';
import Settings from './pages/Settings';
import Demo from './pages/Demo';
import HelpCenter from './pages/HelpCenter';
import DeductibilityRules from './pages/DeductibilityRules';
import AuditLog from './pages/AuditLog';
import Tasks from './pages/Tasks';
import Categories from './pages/Categories';
import SearchPage from './pages/SearchPage';
import SavingsGoals from './pages/SavingsGoals';

// Components
import LoginForm from './components/LoginForm';
import AccountsPayable from './components/AccountsPayable';
import AccountsReceivable from './components/AccountsReceivable';
import AdvancedAnalytics from './components/AdvancedAnalytics';
import AdvancedReports from './components/AdvancedReports';
import InvoiceAutomation from './components/InvoiceAutomation';

// Layout Component
function AppLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Define helper for active tab
  const isActive = (path) => location.pathname === path;

  // Bottom Navigation configuration
  const bottomNav = (
    <>
      <MobileBottomNavItem
        icon="home"
        label="Inicio"
        active={isActive('/')}
        onClick={() => navigate('/')}
      />
      <MobileBottomNavItem
        icon="banknotes"
        label="Movimientos"
        active={isActive('/transactions')}
        onClick={() => navigate('/transactions')}
      />
      <MobileBottomNavItem
        icon="calculator"
        label="Fiscal"
        active={isActive('/fiscal')}
        onClick={() => navigate('/fiscal')}
      />
      <MobileBottomNavItem
        icon="cog"
        label="Ajustes"
        active={isActive('/settings')}
        onClick={() => navigate('/settings')}
      />
    </>
  );

  return (
    <MobileLayout
      title="Avanta Coinmaster"
      showHeader={true}
      bottomNav={bottomNav}
      onTitleClick={() => navigate('/')}
    >
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
    </MobileLayout>
  );
}


// Routes Definition
function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginForm />} />

      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout>
            <Home />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/transactions" element={
        <ProtectedRoute>
          <AppLayout>
            <Transactions />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/accounts" element={
        <ProtectedRoute>
          <AppLayout>
            <Accounts />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/budgets" element={
        <ProtectedRoute>
          <AppLayout>
            <Budgets />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/invoices" element={
        <ProtectedRoute>
          <AppLayout>
            <Invoices />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/credits" element={
        <ProtectedRoute>
          <AppLayout>
            <Credits />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/fiscal" element={
        <ProtectedRoute>
          <AppLayout>
            <Fiscal />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute>
          <AppLayout>
            <Settings />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/categories" element={
        <ProtectedRoute>
          <AppLayout>
            <Categories />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks" element={
        <ProtectedRoute>
          <AppLayout>
            <Tasks />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/goals" element={
        <ProtectedRoute>
          <AppLayout>
            <SavingsGoals />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/search" element={
        <ProtectedRoute>
          <AppLayout>
            <SearchPage />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/audit" element={
        <ProtectedRoute>
          <AppLayout>
            <AuditLog />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/help" element={
        <ProtectedRoute>
          <AppLayout>
            <HelpCenter />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/deductibility" element={
        <ProtectedRoute>
          <AppLayout>
            <DeductibilityRules />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/demo" element={
        <ProtectedRoute>
          <AppLayout>
            <Demo />
          </AppLayout>
        </ProtectedRoute>
      } />

      {/* Automation Routes */}
      <Route path="/payables" element={
        <ProtectedRoute>
          <AppLayout>
            <AccountsPayable />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/receivables" element={
        <ProtectedRoute>
          <AppLayout>
            <AccountsReceivable />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/analytics" element={
        <ProtectedRoute>
          <AppLayout>
            <AdvancedAnalytics />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/reports" element={
        <ProtectedRoute>
          <AppLayout>
            <AdvancedReports />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/invoice-automation" element={
        <ProtectedRoute>
          <AppLayout>
            <InvoiceAutomation />
          </AppLayout>
        </ProtectedRoute>
      } />

      <Route path="/automation" element={
        <ProtectedRoute>
          <AppLayout>
            <div className="p-6">
              <h1 className="text-2xl font-bold mb-4">Automatización</h1>
              <p className="text-gray-600 dark:text-gray-400">Dashboard de automatización en desarrollo.</p>
            </div>
          </AppLayout>
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Main App Component
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
